import { useCallback, useEffect, useState } from "react";

// «Χώρος Εργασίας» (iter109): Κατάστημα + Τμήμα που επιβεβαιώνει ο χρήστης στον Πίνακα Ελέγχου πριν φορτωθούν δεδομένα.
// Αποθηκεύεται ανά browser/χρήστη και γίνεται προεπιλογή στα φίλτρα «Κατάστημα»/«Τύπος» σε Σήμερα, Υποθέσεις, Στατιστικά, Αναφορές.
const EVENT = "themis-workspace-changed";
const keyFor = (user) => `themis_workspace_${user?.id || user?.username || "anon"}`;

export const COURT_TYPE_LABELS = { "": "Όλα τα Τμήματα", "Μονομελές": "Νέα Τακτική Μονομελούς", "Πολυμελές": "Νέα Τακτική Πολυμελούς" };

export const readWorkspace = (user) => {
  try {
    const raw = localStorage.getItem(keyFor(user));
    if (!raw) return null;
    const ws = JSON.parse(raw);
    if (!ws || typeof ws !== "object" || !ws.year) return null; // user Νοέ 2026: το έτος είναι υποχρεωτικό → παλιός χώρος εργασίας χωρίς έτος = επανεπιλογή
    return { branch: ws.branch || "", court_type: ws.court_type || "", year: String(ws.year), confirmed_at: ws.confirmed_at || null };
  } catch { return null; }
};

export const writeWorkspace = (user, ws) => {
  const value = { branch: ws.branch || "", court_type: ws.court_type || "", year: String(ws.year || ""), confirmed_at: new Date().toISOString() };
  localStorage.setItem(keyFor(user), JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(EVENT, { detail: value }));
  return value;
};

export const clearWorkspace = (user) => {
  localStorage.removeItem(keyFor(user));
  window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
};

// «Αμμαρ…» (διπλό μ) → «Αμαρ…»
const fixTypos = (s) => (s || "").replace(/([αΑ])[μΜ][μΜ]([αάΑΆ]?)([ρΡ])/g, "$1μ$2$3");

// Επίσημη ονομασία: «Πρωτοδικείο Αθηνών» / «Πρωτοδικείο Αθηνών Περιφερειακή Έδρα Αμαρουσίου»
export const branchDisplay = (branch, isRegional) => {
  if (!branch) return "Όλα τα Καταστήματα";
  const b = fixTypos(branch).trim();
  if (isRegional === false) return b;
  if (/πρωτοδικε/i.test(b)) return /περ[ιο]φερειακ/i.test(b) ? b : "Πρωτοδικείο Αθηνών";
  const core = b.replace(/περ[ιο]φερειακ[ήη]ς?\s+[έε]δρας?/i, "").replace(/\s+/g, " ").trim();
  return `Πρωτοδικείο Αθηνών Περιφερειακή Έδρα ${core}`;
};

export const workspaceLabel = (ws) => {
  if (!ws) return "";
  const b = branchDisplay(ws.branch);
  const ct = COURT_TYPE_LABELS[ws.court_type || ""] || ws.court_type;
  return `${b} / ${ct}${ws.year ? ` / Έτος ${ws.year}` : ""}`;
};

// Query params που στέλνονται στο backend από τον Χώρο Εργασίας
export const workspaceParams = (ws) => {
  const p = {};
  if (ws?.branch) p.branch = ws.branch;
  if (ws?.court_type) p.court_type = ws.court_type;
  return p;
};

export const useWorkspace = (user) => {
  const [ws, setWs] = useState(() => readWorkspace(user));
  useEffect(() => { setWs(readWorkspace(user)); }, [user?.id, user?.username]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = (e) => setWs(e.detail);
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, []);
  const save = useCallback((next) => setWs(writeWorkspace(user, next)), [user]);
  const clear = useCallback(() => { clearWorkspace(user); setWs(null); }, [user]);
  return { ws, save, clear, label: workspaceLabel(ws) };
};
